import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Empty, Button, Alert } from 'antd';
import { HomeOutlined, MailOutlined } from '@ant-design/icons';
import { getSiteVisitForms } from '../services/apiService';
import authService from '../services/authService';
import FullPageLoader from '../components/FullPageLoader';

export default function SiteVisitFormsList() {
  const { companyId: paramCompanyId } = useParams();
  const [forms, setForms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [userRole, setUserRole] = useState(null);
  const [companyName, setCompanyName] = useState(null);

  const token = authService.get_api_token();

  useEffect(() => {
    let mounted = true;
    (async () => {
      setLoading(true);
      setError(null);

      try {
        // Get user details from /me API
        const userDetails = await authService.getUserDetails();
        //console.log('User details fetched:', userDetails);

        const role = userDetails?.role_id || null;
        const isSuspended = userDetails?.is_suspended ? userDetails?.is_suspended : false || false;
        const company_name = userDetails?.company_name || userDetails?.company_name || null;
        const isSuperAdmin = role === 'superadmin' || role === 'Super Administrator' || role === 'SUPERADMIN';
        
        if (mounted) {
          setUserRole(role);
          setCompanyName(company_name);
        }

        // resolve companyId: route param -> stored user -> /me
        let companyId = paramCompanyId || null;

        if (!companyId) {
          companyId = userDetails?.company_id || userDetails?.company?.id || null;
        }

        if(isSuspended === true){
          if (mounted) {
            setError('User is suspended');
            setLoading(false);
          }
          return;
        }

        if (!companyId) {
          if (mounted) {
            setError('Company ID not available');
            setLoading(false);
          }
          return;
        }

        // Fetch forms
        try {
          // If not superadmin, use user-specific endpoint
          const userId = !isSuperAdmin ? (userDetails?.user_id || userDetails?.id) : null;
          
          // console.log('Fetching forms for:', {
          //   companyId,
          //   userId,
          //   isSuperAdmin,
          //   role
          // });
          
          const resp = await getSiteVisitForms(companyId, userId);

          // normalize response to array of form items
          const list = Array.isArray(resp)
            ? resp
            : Array.isArray(resp?.data)
              ? resp.data
              : Array.isArray(resp?.results)
                ? resp.results
                : Array.isArray(resp?.items)
                  ? resp.items
                  : [];

          // If superadmin, include all forms for the company
          // If regular user, API returns only assigned forms
          
          if (mounted) {
            setForms(list);
            //console.log('Fetched site visit forms:', resp);
            //console.log('User role:', role, 'Is SuperAdmin:', isSuperAdmin);
          }
        } catch (err) {
          console.error('Failed to fetch site visit forms', err);
          if (mounted) setError(err?.message || 'Failed to load forms');
        }
      } catch (err) {
        console.error('Failed to fetch user details', err);
        if (mounted) {
          setError(err?.message || 'Failed to load user information');
        }
      } finally {
        if (mounted) setLoading(false);
      }
    })();

    return () => { mounted = false; };
  }, [paramCompanyId, token]);

  if (loading) return <FullPageLoader message="Loading site visit forms..." />;
  
  if (error) {
    return (
      <Alert
        message="Error"
        description={error}
        type="error"
        showIcon
        style={{ margin: 20 }}
      />
    );
  }

  if (!Array.isArray(forms) || forms.length === 0) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: 500 }}>
        <Empty
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description={
            <div>
              <h3 style={{ marginBottom: 8 }}>No Site Visit Forms Available</h3>
              <p style={{ color: '#666', marginBottom: 16 }}>
                {userRole === 'superadmin' || userRole === 'super_admin' || userRole === 'SUPERADMIN'
                  ? 'No site visit forms exist for this company yet.'
                  : 'No site visit forms have been assigned to you yet.'
                }
              </p>
            </div>
          }
          style={{ padding: 40 }}
        >
          <div style={{ display: 'flex', gap: 12, justifyContent: 'center', flexWrap: 'wrap' }}>
            <Button 
              type="primary" 
              icon={<HomeOutlined />}
              onClick={() => window.location.href = '/'}
            >
              Go to Dashboard
            </Button>
            <Button 
              icon={<MailOutlined />}
              onClick={() => window.location.href = 'mailto:admin@company.com'}
            >
              Contact Admin
            </Button>
          </div>
        </Empty>
      </div>
    );
  }

  // Simple form grid layout (layout container is now in MainLayout)
  return (
    <div>
      <div style={{ marginBottom: 20 }}>
        <h2 style={{ margin: 0, fontSize: 20 }}>
          Available Forms
          {(userRole === 'superadmin' || userRole === 'super_admin' || userRole === 'SUPERADMIN') && (
            <span style={{ fontSize: 14, color: '#666', marginLeft: 8 }}>(All Company Forms)</span>
          )}
        </h2>
        <div style={{ fontSize: 13, color: '#666' }}>{forms.length} form{forms.length > 1 ? 's' : ''}</div>
      </div>

      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))',
        gap: 16
      }}>
        {forms.map((item, idx) => {
          // item shape (from API): { fields: [...], meta: { form_id, image, company_id, name, ... } }
          const fields = item.fields || [];
          const meta = item.meta || {};
          const id = meta.form_id || item.id || item.uuid || idx;
          const logo = (meta.image && meta.image[0] && meta.image[0].public_url) || item.logo || '/logo-placeholder.png';
          const title = meta.name || item.title || item.name || 'Untitled form';
          const companyId = meta.company_id || paramCompanyId || '';

          return (
            <div key={id} style={{
              border: '1px solid #e9eef2',
              borderRadius: 8,
              padding: 12,
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'space-between',
              minHeight: 160,
              background: '#fff',
              transition: 'box-shadow 0.2s',
              cursor: 'pointer'
            }}
            onMouseEnter={(e) => e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.1)'}
            onMouseLeave={(e) => e.currentTarget.style.boxShadow = 'none'}
            >
              <div>
                <div style={{ display: 'flex', alignItems: 'top', gap: 20 }}>
                  <img
                    src={logo}
                    alt="logo"
                    style={{ width: 48, height: 48, objectFit: 'cover', borderRadius: 6 }}
                  />
                  <div>
                    <div style={{ fontSize: 14, fontWeight: 600 }}>{title}</div>
                    <div style={{ fontSize: 12, color: '#666', marginTop: 10 }}>{meta.company_name ? `Company: ${meta.company_name}` : `Company: ${companyName}`}</div>
                    <div style={{ fontSize: 12, color: '#999', marginTop: 10 }}>{fields.length} fields</div>
                  </div>
                </div>
              </div>

              <div style={{ marginTop: 12, textAlign: 'right' }}>
                <Link
                  to={`/companies/${companyId}/site_visit_forms/${id}/view`}
                  style={{
                    display: 'inline-block',
                    padding: '8px 12px',
                    background: '#1976d2',
                    color: '#fff',
                    borderRadius: 6,
                    textDecoration: 'none',
                    transition: 'background 0.2s'
                  }}
                  onMouseEnter={(e) => e.currentTarget.style.background = '#1565c0'}
                  onMouseLeave={(e) => e.currentTarget.style.background = '#1976d2'}
                >
                  View
                </Link>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}